import { useLayoutEffect, useRef, useState, useEffect } from "react";
import { useLocation, useNavigationType, useOutlet } from "react-router-dom";
import { resolveTransitionVariant } from "./routeTransitionVariant";
import { scrollSurfacesToTop } from "./scrollSurfacesToTop";
import rt from "./profileRouteTransition.module.css";

/**
 * Canonical player-platform route enter transition (forward / back / fade).
 * Mount once at the app route layout — see docs/DESIGN/VISUAL_LANGUAGE_V2.md § Motion.
 *
 * Important: animation classes are cleared after `animationend` so leftover
 * `transform` / `will-change` cannot trap `position:fixed` chrome (AppShell bottom nav).
 */
export default function ProfileRouteTransition() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const outlet = useOutlet();
  const skipAnimationRef = useRef(true);
  const prevPathRef = useRef(location.pathname);
  const pageRef = useRef(null);
  const [motionClass, setMotionClass] = useState("");

  useLayoutEffect(() => {
    scrollSurfacesToTop();
  }, [location.pathname, location.key]);

  useLayoutEffect(() => {
    skipAnimationRef.current = false;
  }, []);

  const variant = resolveTransitionVariant({
    skipAnimation: skipAnimationRef.current,
    navigationType,
    fromPath: prevPathRef.current,
    toPath: location.pathname,
  });

  useLayoutEffect(() => {
    prevPathRef.current = location.pathname;
    const next = rt[`enter-${variant}`] || "";
    setMotionClass(next);
  }, [location.pathname, location.key, variant]);

  useEffect(() => {
    const node = pageRef.current;
    if (!node || !motionClass) return undefined;
    const clear = () => setMotionClass("");
    // `enter-initial` has no animation — clear immediately so fill styles never linger.
    if (motionClass.includes("enter-initial") || motionClass.includes("enterInitial")) {
      clear();
      return undefined;
    }
    node.addEventListener("animationend", clear);
    const fallback = window.setTimeout(clear, 500);
    return () => {
      node.removeEventListener("animationend", clear);
      window.clearTimeout(fallback);
    };
  }, [motionClass, location.key]);

  return (
    <div className={rt.stage} data-route-stage="">
      <div
        key={location.key}
        ref={pageRef}
        data-route-page=""
        className={[rt.page, motionClass].filter(Boolean).join(" ")}
      >
        {outlet}
      </div>
    </div>
  );
}
